import { css, cx } from "@emotion/css";
import type { OctopusTheme } from "@octopusdeploy/design-system-tokens";
import { lightTheme, textTheme } from "@octopusdeploy/design-system-tokens";
import type { PropsWithChildren } from "react";
import React, { createContext, useMemo } from "react";
import { scrollbarStyles } from "./components/ScrollArea/ScrollArea";
import { getTheme } from "./utils/getTheme";
export interface ThemeProps {
    className?: string;
    themeName: ThemeName;
    customFontOverride?: string;
    isFullHeight?: boolean;
}
const DesignSystemThemeContext = createContext<OctopusTheme | undefined>(undefined);
export const DesignSystemThemeProvider = DesignSystemThemeContext.Provider;
export function useDesignSystemTheme() {
    const contextValue = React.useContext(DesignSystemThemeContext);
    if (contextValue === undefined) {
        throw new Error("Context value not provided via a DesignSystemThemeProvider");
    }
    return contextValue;
}
export function useIllustrationThemeName(): IllustrationThemeName {
    const currentTheme = useDesignSystemTheme();
    return currentTheme === lightTheme ? "light" : "dark";
}
export function Theme({ className, themeName, customFontOverride, isFullHeight, children }: PropsWithChildren<ThemeProps>) {
    const theme: OctopusTheme = getTheme(themeName);
    const textStylesWithOverride = useMemo(() => css({
        ...textTheme,
        fontFamilyDefault: customFontOverride ?? textTheme.fontFamilyDefault,
    }), [customFontOverride]);
    const themeCssVariableStyles = useMemo(() => css(Object.fromEntries(Object.entries(theme).map(([name, value]) => [`--${name}`, value]))), [theme]);
    return (<DesignSystemThemeProvider value={theme}>
            <div className={cx(textStylesWithOverride, themeCssVariableStyles, themeContentStyles, scrollbarStyles, { [fullHeightStyles]: isFullHeight }, className)}>{children}</div>
        </DesignSystemThemeProvider>);
}
const fullHeightStyles = css({
    height: "100%",
});
const themeContentStyles = css({
    width: "100%",
});
type IllustrationThemeName = "light" | "dark";
export type ThemeName = "light" | "dark";
